import React, { useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import Dashboard from "./pages/dashboard";
import { useAuth0 } from "@auth0/auth0-react";
import NotAuthorized from "@src/pages/not_authorized";
import UserIndex from "@src/pages/users";
import UserEdit from "@src/pages/users/edit";
import ProjectIndex from "@src/pages/projects";
import ProjectEdit from "@src/pages/projects/edit";
import BlogEdit from "@src/pages/blog/edit";
import BlogIndex from "./pages/blog/index";
import OrganizationIndex from "@src/pages/organizations";
import OrganizationEdit from "@src/pages/organizations/edit";
import TalentApplicationIndex from "@src/pages/talent-applications";
import TalentApplicationReview from "@src/pages/talent-applications/review";
import CreatorApplicationIndex from "@src/pages/creator-applications";
import CreatorApplicationReview from "@src/pages/creator-applications/review";
import InvitationCodeIndex from "@src/pages/invitation_codes";
import InvitationCodeEdit from "@src/pages/invitation_codes/edit";
import PromptIndex from "@src/pages/prompt";
import PromptEdit from "./pages/prompt/edit";
import VaultIndex from "./pages/vaults";
import VaultVariationIndex from "./pages/vaults/variations";
import TestScenarios from "./pages/vaults/test-scenarios";
import BusinessProfileIndex from "./pages/business-profiles";
import BusinessProfileEdit from "./pages/business-profiles/edit";
import CampaignEdit from "./pages/business-profiles/campaign";
import DocumentIndex from "./pages/documents";
import VideoGeneratorIndex from "@src/pages/video";
import VoiceBlueprintIndex from "@src/pages/voice";
import CaptionsIndex from "@src/pages/submagic";
import CustomerIndex from "@src/pages/customers";
import ProductIndex from "@src/pages/products";
import ProductEdit from "@src/pages/products/edit";
import AudioTrackIndex from "@src/pages/audio-tracks";
import AudioTrackEdit from "@src/pages/audio-tracks/edit";
import VoiceTrainingScriptIndex from "@src/pages/voice-training-scripts";
import VoiceTrainingScriptEdit from "@src/pages/voice-training-scripts/edit";
import LicensablePropertyIndex from "@src/pages/licensable-properties/index";
import LicensablePropertyEdit from "@src/pages/licensable-properties/edit";

function Router() {
  const { loginWithRedirect, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasAttemptedLogin) {
      setHasAttemptedLogin(true);
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, hasAttemptedLogin]);

  useEffect(() => {
    if (isAuthenticated) {
      const refreshToken = async () => {
        try {
          await getAccessTokenSilently();
        } catch (error) {
          console.error("Error refreshing token:", error);
        }
      };
      refreshToken();
      const intervalId = setInterval(refreshToken, 30 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const router = createBrowserRouter([
    { path: "/", element: <Dashboard /> },
    { path: "/not-authorized", element: <NotAuthorized /> },
    { path: "/blog", element: <BlogIndex /> },
    { path: "/blog/edit/:id", element: <BlogEdit /> },
    { path: "/blog/add", element: <BlogEdit /> },
    { path: "/users", element: <UserIndex /> },
    { path: "/users/edit/:id", element: <UserEdit /> },
    { path: "/users/add", element: <UserEdit /> },
    { path: "/organizations", element: <OrganizationIndex /> },
    { path: "/organizations/edit/:id", element: <OrganizationEdit /> },
    { path: "/organizations/add", element: <OrganizationEdit /> },
    { path: "/projects", element: <ProjectIndex /> },
    { path: "/projects/edit/:id", element: <ProjectEdit /> },
    { path: "/projects/add", element: <ProjectEdit /> },
    { path: "/talent-applications", element: <TalentApplicationIndex /> },
    { path: "/talent-applications/review/:id", element: <TalentApplicationReview /> },
    { path: "/creator-applications", element: <CreatorApplicationIndex /> },
    { path: "/creator-applications/review/:id", element: <CreatorApplicationReview /> },
    { path: "/invitation-codes", element: <InvitationCodeIndex /> },
    { path: "/invitation-codes/edit/:id", element: <InvitationCodeEdit /> },
    { path: "/invitation-codes/add", element: <InvitationCodeEdit /> },
    { path: "/prompts", element: <PromptIndex /> },
    { path: "/prompts/edit/:id", element: <PromptEdit /> },
    { path: "/prompts/edit/add", element: <PromptEdit /> },
    { path: "/vaults", element: <VaultIndex /> },
    { path: "/vault/variations", element: <VaultVariationIndex /> },
    { path: "/vault/test-scenarios", element: <TestScenarios /> },
    { path: "/business-profiles", element: <BusinessProfileIndex /> },
    { path: "/business-profiles/edit/:id", element: <BusinessProfileEdit /> },
    { path: "/business-profiles/add", element: <BusinessProfileEdit /> },
    { path: "/business-profiles/:businessProfileId/campaigns", element: <CampaignEdit /> },
    { path: "/documents", element: <DocumentIndex /> },
    { path: "/video", element: <VideoGeneratorIndex /> },
    { path: "/voice", element: <VoiceBlueprintIndex /> },
    { path: "/captions", element: <CaptionsIndex /> },
    { path: "/customers", element: <CustomerIndex /> },
    { path: "/products", element: <ProductIndex /> },
    { path: "/products/edit/:id", element: <ProductEdit /> },
    { path: "/products/add", element: <ProductEdit /> },
    { path: "/voice-training-scripts", element: <VoiceTrainingScriptIndex /> },
    { path: "/voice-training-scripts/edit/:id", element: <VoiceTrainingScriptEdit /> },
    { path: "/voice-training-scripts/add", element: <VoiceTrainingScriptEdit /> },
    { path: "/audio-tracks", element: <AudioTrackIndex /> },
    { path: "/audio-tracks/edit/:id", element: <AudioTrackEdit /> },
    { path: "/audio-tracks/add", element: <AudioTrackEdit /> },
    { path: "/licensable-properties", element: <LicensablePropertyIndex /> },
    { path: "/licensable-properties/edit/:id", element: <LicensablePropertyEdit /> },
    { path: "/licensable-properties/add", element: <LicensablePropertyEdit /> },
  ]);

  if (isLoading || (!isAuthenticated && hasAttemptedLogin)) {
    return <div>Logging in...</div>;
  }

  return <RouterProvider router={router} />;
}

export default Router;
